import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import api from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ArrowLeft } from 'lucide-react'
import type { AuditLogResponse, PaginatedResponse, TenantResponse } from '@/api/types'

const PRODUCT_NAMES: Record<number, string> = { 1: 'PAM', 2: 'Data AI', 3: 'BetTalk' }

function actionColor(action: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (action.includes('delete') || action.includes('remove')) return 'destructive'
  if (action.includes('create') || action.includes('add')) return 'default'
  if (action.includes('update') || action.includes('bind')) return 'secondary'
  return 'outline'
}

function tryFormatJSON(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2)
  } catch {
    return s
  }
}

export function AuditDetailPage() {
  const { t } = useTranslation(['audit', 'common'])
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data, isLoading, error } = useQuery({
    queryKey: ['audit', id],
    queryFn: () => api.get<AuditLogResponse>(`/audit/${id}`),
    enabled: !!id,
  })

  const { data: tenantsData } = useQuery({
    queryKey: ['tenants-all'],
    queryFn: () => api.get<PaginatedResponse<TenantResponse>>('/tenants?offset=0&limit=100'),
  })
  const tenantLookup = Object.fromEntries((tenantsData?.data.data ?? []).map((t) => [t.id, t.name]))

  const l = data?.data

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/audit')}>
          <ArrowLeft className="h-4 w-4 mr-1" />{t('buttons.back', { ns: 'common' })}
        </Button>
        <h2 className="text-2xl font-semibold">{t('detail.title', { id })}</h2>
      </div>

      {isLoading && <p className="text-muted-foreground">{t('loading', { ns: 'common' })}</p>}
      {error && <p className="text-destructive">{t('detail.loadError')}</p>}

      {l && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <Badge variant={actionColor(l.action)} className="text-sm">{l.action}</Badge>
              <span className="text-base font-normal text-muted-foreground">
                {l.entity_type}{l.entity_id ? ` #${l.entity_id}` : ''}
              </span>
              {l.impersonated_by != null && (
                <Badge variant="destructive" className="text-xs">
                  {t('impersonatedBy')} {l.impersonated_by_email ?? `#${l.impersonated_by}`}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-y-4 gap-x-6 text-sm">
              <div>
                <span className="text-xs text-muted-foreground block">{t('fields.id')}</span>
                <span className="font-mono">{l.id}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">{t('fields.timestamp')}</span>
                <span>{new Date(l.created_at).toLocaleString()}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">{t('fields.action')}</span>
                <Badge variant={actionColor(l.action)}>{l.action}</Badge>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">{t('fields.actor')}</span>
                <span>{l.actor_email ?? (l.actor_id != null ? `#${l.actor_id}` : '—')}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">{t('fields.impersonatedBy')}</span>
                <span>
                  {l.impersonated_by != null
                    ? <><span>{l.impersonated_by_email ?? `#${l.impersonated_by}`}</span> <Badge variant="destructive" className="ml-1 text-xs">{t('impBadge')}</Badge></>
                    : '—'}
                </span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">{t('fields.entity')}</span>
                <span>{l.entity_type}{l.entity_id ? ` #${l.entity_id}` : ''}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">{t('fields.tenant')}</span>
                <span>{l.tenant_id != null ? (tenantLookup[l.tenant_id] ?? l.tenant_id) : '—'}</span>
              </div>
              <div>
                <span className="text-xs text-muted-foreground block">{t('fields.product')}</span>
                <span>{l.product_id != null ? (PRODUCT_NAMES[l.product_id] ?? l.product_id) : '—'}</span>
              </div>
            </div>
            {l.details && (
              <div className="mt-4">
                <span className="text-xs text-muted-foreground block mb-1">{t('fields.details')}</span>
                <pre className="text-xs bg-muted/40 rounded-md p-3 overflow-auto max-h-48">{tryFormatJSON(l.details)}</pre>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
